// Display languages, language resolution and the Han search transforms
// (doc/20261023-i18n-zh-cn.md §3.1, §3.2, §3.7).
//
// Plain ES-module JavaScript with a hand-written locale.d.mts, like shared/identity/mailbox.mjs: the daemon and
// control import it by relative path, the web apps through Vite. It must stay dependency-free.

/** Every display language this build ships, in menu order. */
export const SUPPORTED_DISPLAY_LANGUAGES = Object.freeze([
  Object.freeze({
    id: 'en', nativeName: 'English', englishName: 'English', script: 'Latn', antd: 'en_US', dayjs: 'en',
  }),
  Object.freeze({
    id: 'zh-Hans-CN', nativeName: '简体中文', englishName: 'Chinese (Simplified, Mainland China)', script: 'Hans',
    antd: 'zh_CN', dayjs: 'zh-cn',
  }),
]);

export const DEFAULT_DISPLAY_LANGUAGE = 'en';

const SUPPORTED_IDS = new Set(SUPPORTED_DISPLAY_LANGUAGES.map((language) => language.id));

export function isSupportedDisplayLanguage(value) {
  return typeof value === 'string' && SUPPORTED_IDS.has(value);
}

export function displayLanguageInfo(id) {
  return SUPPORTED_DISPLAY_LANGUAGES.find((language) => language.id === id) ?? null;
}

/**
 * Classifies one BCP 47 tag (or a stored id).
 * - `{kind: 'supported', id}` for a tag this build can show
 * - `{kind: 'traditional'}` for Traditional Chinese, which this build doesn't ship: it sets Traditional intent
 * - `{kind: 'unsupported'}` for anything else
 */
export function classifyLanguageTag(tag) {
  if (typeof tag !== 'string') return { kind: 'unsupported' };
  const parts = tag.trim().replaceAll('_', '-').toLowerCase().split('-');
  const language = parts[0];
  if (!/^[a-z]{2,3}$/u.test(language ?? '')) return { kind: 'unsupported' };
  // BCP 47 positions: language, up to three 3-letter extended-language subtags (`zh-cmn-…`), then an optional 4-letter
  // script, then an optional region (2 letters or 3 digits). Variants, extensions and private use carry no meaning here.
  let position = 1;
  while (position < 4 && /^[a-z]{3}$/u.test(parts[position] ?? '')) position += 1;
  const script = /^[a-z]{4}$/u.test(parts[position] ?? '') ? parts[position++] : undefined;
  const region = /^(?:[a-z]{2}|\d{3})$/u.test(parts[position] ?? '') ? parts[position] : undefined;
  if (language === 'en') return { kind: 'supported', id: 'en' };
  if (language !== 'zh') return { kind: 'unsupported' };
  // An explicit script decides; without one, the region implies it.
  const traditional = script === 'hant' || (script === undefined && (region === 'tw' || region === 'hk' || region === 'mo'));
  // No Traditional catalogue yet: zh-Hant-HK / zh-Hant-TW join SUPPORTED_DISPLAY_LANGUAGES in a later slice.
  if (traditional) return { kind: 'traditional' };
  if (script !== undefined && script !== 'hans') return { kind: 'unsupported' };
  return { kind: 'supported', id: 'zh-Hans-CN' };
}

function isChinese(id) {
  return id.startsWith('zh-');
}

/**
 * Resolves the display language from an ordered list of steps (§3.2): pending/stored preference, device choice,
 * zone default, browser/Office languages. Each step is a list of candidate tags; `null`/`undefined` entries are
 * ignored.
 *
 * Traditional intent: once a Traditional tag (or an id for a Traditional language this build doesn't ship) has
 * been seen, every later Chinese candidate is skipped, so a Traditional reader never lands in Simplified.
 */
export function resolveDisplayLanguage(steps) {
  let traditional = false;
  for (const step of steps ?? []) {
    const tags = Array.isArray(step) ? step : [step];
    for (const tag of tags) {
      if (tag === null || tag === undefined) continue;
      const classified = classifyLanguageTag(tag);
      if (classified.kind === 'traditional') {
        traditional = true;
        continue;
      }
      if (classified.kind !== 'supported') continue;
      if (traditional && isChinese(classified.id)) continue;
      return classified.id;
    }
  }
  return DEFAULT_DISPLAY_LANGUAGE;
}

// ---------------------------------------------------------------- search (§3.7)

const HAN = /\p{Script=Han}/u;
// NUL ends an FTS5 string literal early; as a separator it changes nothing the tokenizer would keep.
const NUL = /\u0000/gu;

/** Splits text into runs: `{han: true, text}` for maximal Han runs, `{han: false, text}` for everything else. */
function runs(text) {
  const out = [];
  for (const character of text) {
    const han = HAN.test(character);
    const last = out.at(-1);
    if (last !== undefined && last.han === han) last.text += character;
    else out.push({ han, text: character });
  }
  return out;
}

function hanTokens(run, final) {
  const characters = Array.from(run);
  if (characters.length === 1) return characters;
  const tokens = [];
  for (let index = 0; index + 1 < characters.length; index += 1) tokens.push(characters[index] + characters[index + 1]);
  // A run bounded on its right also carries its trailing unigram, as the index does.
  if (!final) tokens.push(characters.at(-1));
  return tokens;
}

/**
 * The text written into `chunks_fts_v2`: NFKC-normalised, a space at every Han / non-Han boundary, and every Han run
 * rewritten as its overlapping bigrams plus a trailing unigram (a one-character run stays one character).
 */
export function hanIndexText(text) {
  return runs(String(text).normalize('NFKC').replace(NUL, ' '))
    .map((run) => (run.han ? ` ${hanTokens(run.text, false).join(' ')} ` : run.text))
    .join('');
}

function quote(text) {
  return `"${text.replaceAll('"', '""')}"`;
}

/**
 * The FTS5 MATCH expression for a search box query against `chunks_fts_v2`, or `''` for an empty query. Terms split at
 * whitespace only and each becomes one phrase, exactly as the old builder quotes them, so a term with no Han text
 * keeps today's phrase semantics; terms are ANDed.
 *
 * Only a Han run at the very end of a term may continue in a document (违约 inside 违约责任). Anything after a run —
 * letters, digits, punctuation, emoji — bounds it there in the document too, so such a run carries its trailing
 * unigram, exactly as the index wrote it. Which characters the tokenizer keeps does not matter to this rule.
 */
export function ftsMatchForQuery(query) {
  const phrases = [];
  for (const term of String(query).normalize('NFKC').replace(NUL, ' ').match(/\S+/gu) ?? []) {
    const parts = runs(term);
    const finalIndex = parts.length - 1;
    const pieces = [];
    let prefix = false;
    parts.forEach((part, index) => {
      if (!part.han) {
        pieces.push(part.text);
        return;
      }
      const final = index === finalIndex;
      if (final && Array.from(part.text).length === 1) prefix = true;
      pieces.push(hanTokens(part.text, final).join(' '));
    });
    // Separators between pieces keep Han tokens apart from adjacent letters, exactly as hanIndexText does.
    phrases.push(quote(pieces.join(' ')) + (prefix ? ' *' : ''));
  }
  return phrases.join(' ');
}
